import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { removeToken, getSuperAdminData } from "../utils/tokenUtils";

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const superAdmin = getSuperAdminData();

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => (
    window.localStorage.getItem('superadmin-sidebar-collapsed') === 'true'
  ));
  const [collapseButtonHovered, setCollapseButtonHovered] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const lastScrollYRef = useRef(0);
  const isCompactMobile = typeof window !== 'undefined' && window.innerWidth <= 360;
  const desktopSidebarWidth = sidebarCollapsed ? 72 : 260;
  const contentOffset = !isMobile && sidebarOpen ? desktopSidebarWidth : 0;
  const usernameInitial = (superAdmin?.username || 'A').trim().charAt(0).toUpperCase();
  const userDisplayName = superAdmin?.username || 'Super Admin';
  const userEmail = superAdmin?.email || `${(superAdmin?.username || 'superadmin').toLowerCase()}@gmail.com`;

  useEffect(() => {
    window.localStorage.setItem('superadmin-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // If switching to desktop, open sidebar; if switching to mobile, close sidebar
      setSidebarOpen(!mobile);
      if (mobile) setSidebarCollapsed(false);
    };
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;

    const handleOutsideClick = (event) => {
      if (!event.target.closest('.superadmin-user-menu')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [userMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollYRef.current && currentScrollY > 12;

      if (currentScrollY <= 12) {
        setHeaderVisible(true);
      } else if (scrollingDown) {
        setHeaderVisible(false);
      } else {
        setHeaderVisible(true);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    removeToken();
    setUserMenuOpen(false);
    navigate('/login');
  };

  return (
    <>
      <style>{`
        @keyframes superadminMenuIn {
          from {
            opacity: 0;
            transform: translateY(-6px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
      <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc', overflowX: 'hidden' }}>
      <Sidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isCollapsed={sidebarCollapsed}
        toggleCollapsed={() => setSidebarCollapsed((collapsed) => !collapsed)}
      />
      
      <div 
        style={{
          marginLeft: !isMobile && sidebarOpen ? `${desktopSidebarWidth}px` : '0',
          transition: 'margin-left 0.3s cubic-bezier(.4,2,.6,1)',
          width: !isMobile && sidebarOpen ? `calc(100% - ${desktopSidebarWidth}px)` : '100%',
          minWidth: 0,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          position: 'relative',
          paddingTop: isMobile ? 60 : 76,
        }}
      >
        {/* Top Header */}
        <header style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          padding: isCompactMobile ? '10px 12px' : '15px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: headerVisible ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
          position: 'fixed',
          top: 0,
          left: isMobile ? 0 : contentOffset,
          right: 0,
          zIndex: isMobile ? 500 : 1100,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-110%)',
          opacity: headerVisible ? 1 : 0,
          transition: 'transform 0.28s ease, opacity 0.28s ease, box-shadow 0.28s ease, left 0.25s ease',
          width: isMobile ? '100%' : `calc(100% - ${contentOffset}px)`,
          maxWidth: '100%',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <button
                onClick={toggleSidebar}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: isCompactMobile ? '18px' : '20px',
                  cursor: 'pointer',
                  padding: isCompactMobile ? '6px' : '8px',
                   borderRadius: '8px',
                  marginRight: isCompactMobile ? '8px' : '15px',
                  transition: 'background-color 0.2s ease',
                  fontWeight: 'bold'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ≡
              </button>
            )}
            {!isMobile && (
              <button
                type="button"
                onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onMouseEnter={() => setCollapseButtonHovered(true)}
                onMouseLeave={() => setCollapseButtonHovered(false)}
                style={{
                  position: 'relative',
                  display: 'grid',
                  placeItems: 'center',
                  width: isCompactMobile ? '30px' : '36px',
                  height: isCompactMobile ? '30px' : '36px',
                  marginRight: isCompactMobile ? '10px' : '12px',
                  padding: 0,
                  border: '1px solid #e2e8f0',
                  borderRadius: '9px',
                  backgroundColor: '#f8fafc',
                  color: '#334155',
                  cursor: 'pointer',
                  fontSize: isCompactMobile ? '18px' : '20px',
                  lineHeight: 1,
                  marginLeft: isCompactMobile ? '2px' : '0'
                }}
              >
                {sidebarCollapsed ? '»' : '«'}
                {collapseButtonHovered && (
                  <span style={{
                    position: 'absolute',
                    top: '50%',
                    left: 'calc(100% + 10px)',
                    transform: 'translateY(-50%)',
                    zIndex: 1200,
                    padding: '9px 12px',
                    borderRadius: '8px',
                    color: '#fff',
                    backgroundColor: '#172033',
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.24)',
                    fontSize: '13px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none'
                  }}>
                    {sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  </span>
                )}
              </button>
            )}
            <h1 style={{ 
              margin: 0, 
              fontSize: isCompactMobile ? '16px' : '20px', 
              fontWeight: '600', 
              color: '#1e293b' 
            }}>
              Super Admin Panel
            </h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }} className="superadmin-user-menu">
            <button
              type="button"
              onClick={() => setUserMenuOpen((open) => !open)}
              aria-label="Open user menu"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '1px solid #1f2937',
                background: 'linear-gradient(135deg, #111827 0%, #334155 100%)',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                boxShadow: '0 3px 10px rgba(15, 23, 42, 0.18)',
                padding: 0,
              }}
            >
              {usernameInitial}
            </button>

            {userMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 12px)',
                  right: 0,
                  width: 290,
                  background: '#fff',
                  border: '1px solid #dfe7f2',
                  borderRadius: 14,
                  boxShadow: '0 18px 36px rgba(15, 23, 42, 0.14)',
                  padding: '14px 14px 12px',
                  zIndex: 1300,
                  opacity: 1,
                  transform: 'translateY(0)',
                  transformOrigin: 'top right',
                  transition: 'opacity 0.18s ease, transform 0.18s ease',
                  animation: 'superadminMenuIn 0.18s ease',
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userDisplayName}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 4 }}>
                    {userEmail}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    border: '1px solid #dc2626',
                    background: '#dc2626',
                    color: '#ffffff',
                    borderRadius: 10,
                    padding: '10px 12px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 6px 14px rgba(220, 38, 38, 0.22)',
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    style={{
                      width: 17,
                      height: 17,
                      fill: 'none',
                      stroke: 'currentColor',
                      strokeWidth: 1.8,
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                      flexShrink: 0,
                    }}
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Log out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            padding: isCompactMobile ? '12px' : '20px',
            paddingTop: isCompactMobile ? '64px' : '78px',
            overflowY: 'visible',
            overflowX: 'hidden',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {children}
        </main>
      </div>
    </div>
    </>
  );
};

export default Layout;